import { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Calendar, ImageIcon, AlertCircle, Bold, Italic, Underline, Link, List, ListOrdered } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { newsService } from '@/services/storage';
import { uploadImage } from '@/services/upload';
import type { News } from '@/types';
import { toast } from 'sonner';

export default function AdminNews() {
  const [news, setNews] = useState<News[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState<News | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    image: '',
    date: new Date().toISOString().split('T')[0],
    category: 'company' as 'company' | 'industry',
  });
  const [errorDialog, setErrorDialog] = useState<{open: boolean; title: string; message: string}>({
    open: false,
    title: '',
    message: ''
  });
  const contentImageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    setNews(await newsService.getAll());
  };

  const showError = (title: string, message: string) => {
    setErrorDialog({ open: true, title, message });
  };

  const handleAdd = async () => {
    setEditingNews(null);
    setFormData({
      title: '',
      summary: '',
      content: '',
      image: '',
      date: new Date().toISOString().split('T')[0],
      category: 'company',
    });
    setDialogOpen(true);
  };

  const handleEdit = async (item: News) => {
    setEditingNews(item);
    setFormData({
      title: item.title,
      summary: item.summary || '',
      content: item.content || '',
      image: item.image || '',
      date: item.date,
      category: item.category,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('确定要删除此新闻吗？')) {
      await newsService.delete(id);
      toast.success('新闻已删除');
      loadNews();
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[上传调试] 触发 onChange 事件', e.target.files);
    const file = e.target.files?.[0];
    if (!file) {
      console.log('[上传调试] 未选择文件');
      return;
    }

    console.log('[上传调试] 选择的文件:', file.name, file.type, file.size);

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      console.log('[上传调试] 文件类型错误:', file.type);
      showError('文件类型错误', '请上传图片文件（支持 JPG、PNG、GIF 格式）');
      return;
    }

    // 验证文件大小（最大 5MB）
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      console.log('[上传调试] 文件过大:', file.size);
      showError('文件过大', `图片大小不能超过 5MB，当前文件大小为 ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    try {
      console.log('[上传调试] 开始上传到服务器...');
      const imageUrl = await uploadImage(file);
      console.log('[上传调试] 上传成功, URL:', imageUrl);
      setFormData({ ...formData, image: imageUrl });
      toast.success('图片上传成功');
    } catch (error) {
      console.error('[上传调试] 上传失败:', error);
      showError('上传失败', error instanceof Error ? error.message : '图片上传失败，请重试');
    }
  };

  const handleContentImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showError('文件类型错误', '请上传图片文件');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showError('文件过大', `图片大小不能超过 5MB`);
      return;
    }

    try {
      const imageUrl = await uploadImage(file);
      const textarea = document.getElementById('content') as HTMLTextAreaElement;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = formData.content || '';
        const before = text.substring(0, start);
        const after = text.substring(end);
        const imgTag = `<img src="${imageUrl}" alt="" style="max-width:100%;display:block;margin:12px 0;" />`;
        const newText = before + imgTag + after;
        setFormData({ ...formData, content: newText });
        setTimeout(() => {
          textarea.focus();
          const newCursor = start + imgTag.length;
          textarea.setSelectionRange(newCursor, newCursor);
        }, 0);
      }
      toast.success('图片已插入正文');
    } catch (error) {
      showError('上传失败', error instanceof Error ? error.message : '图片上传失败');
    }

    // 清空input，允许再次上传同一文件
    if (contentImageInputRef.current) {
      contentImageInputRef.current.value = '';
    }
  };

  const insertTag = (tag: string) => {
    const textarea = document.getElementById('content') as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.content || '';
    const before = text.substring(0, start);
    const selected = text.substring(start, end);
    const after = text.substring(end);
    
    let newText = '';
    switch(tag) {
      case 'bold':
        newText = `${before}<strong>${selected || '粗体文字'}</strong>${after}`;
        break;
      case 'italic':
        newText = `${before}<em>${selected || '斜体文字'}</em>${after}`;
        break;
      case 'underline':
        newText = `${before}<u>${selected || '下划线文字'}</u>${after}`;
        break;
      case 'link':
        newText = `${before}<a href="https://">${selected || '链接文字'}</a>${after}`;
        break;
      case 'ul':
        newText = `${before}<ul>\n  <li>列表项</li>\n  <li>列表项</li>\n</ul>${after}`;
        break;
      case 'ol':
        newText = `${before}<ol>\n  <li>列表项</li>\n  <li>列表项</li>\n</ol>${after}`;
        break;
      case 'br':
        newText = `${before}<br/>${after}`;
        break;
      case 'sup':
        newText = `${before}<sup>${selected || '上标文字'}</sup>${after}`;
        break;
      case 'sub':
        newText = `${before}<sub>${selected || '下标文字'}</sub>${after}`;
        break;
      default:
        return;
    }
    
    setFormData({ ...formData, content: newText });
    setTimeout(() => {
      textarea.focus();
      const newCursor = start + (newText.length - text.length);
      textarea.setSelectionRange(newCursor, newCursor);
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      showError('缺少标题', '请填写新闻标题');
      return;
    }

    try {
      if (editingNews) {
        await newsService.update(editingNews.id, formData);
        toast.success('新闻已更新');
      } else {
        const newNews: News = {
          id: Date.now().toString(),
          ...formData,
        };
        await newsService.add(newNews);
        toast.success('新闻已添加');
      }

      setDialogOpen(false);
      loadNews();
    } catch (error: any) {
      console.error('保存新闻失败:', error);
      showError('保存失败', error?.message || '保存新闻时出错，请重试');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">新闻管理</h1>
        <Button onClick={handleAdd}>
          <Plus className="mr-2 h-5 w-5" />
          发布新闻
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>标题</TableHead>
                <TableHead>分类</TableHead>
                <TableHead>日期</TableHead>
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {news.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="font-medium">{item.title}</div>
                    <div className="text-sm text-gray-500 truncate max-w-md">
                      {item.summary}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.category === 'company' ? 'default' : 'secondary'}>
                      {item.category === 'company' ? '公司动态' : '行业新闻'}
                    </Badge>
                  </TableCell>
                  <TableCell>{item.date}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {news.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              暂无新闻数据
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        if (!open) {
          setDialogOpen(false);
          setEditingNews(null);
        } else {
          setDialogOpen(open);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingNews ? '编辑新闻' : '发布新闻'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">
                标题 <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="请输入新闻标题"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">分类</Label>
                <select
                  id="category"
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                >
                  <option value="company">公司动态</option>
                  <option value="industry">行业新闻</option>
                </select>
              </div>
              <div>
                <Label htmlFor="date">日期</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="summary">摘要</Label>
              <textarea
                id="summary"
                className="w-full border rounded-md px-3 py-2"
                rows={2}
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                placeholder="请输入新闻摘要"
              />
            </div>
            <div>
              <Label htmlFor="image">
                <span className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  封面图片
                </span>
              </Label>
              <div className="mt-2">
                {formData.image && (
                  <img
                    src={formData.image}
                    alt="News"
                    className="w-full h-32 object-cover rounded mb-2"
                  />
                )}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
                <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF 格式，最大 5MB</p>
              </div>
            </div>
            <div>
              <Label htmlFor="content">正文内容</Label>
              <div className="mt-2 border rounded-md">
                <div className="flex items-center gap-1 p-2 border-b bg-gray-50">
                  <Button type="button" variant="ghost" size="sm" onClick={() => insertTag('bold')} title="粗体">
                    <Bold className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => insertTag('italic')} title="斜体">
                    <Italic className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => insertTag('underline')} title="下划线">
                    <Underline className="h-4 w-4" />
                  </Button>
                  <div className="w-px h-6 bg-gray-300 mx-1" />
                  <Button type="button" variant="ghost" size="sm" onClick={() => insertTag('sup')} title="上标">
                    <span className="text-xs font-bold leading-none">X<sup className="text-[8px]">2</sup></span>
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => insertTag('sub')} title="下标">
                    <span className="text-xs font-bold leading-none">X<sub className="text-[8px]">2</sub></span>
                  </Button>
                  <div className="w-px h-6 bg-gray-300 mx-1" />
                  <Button type="button" variant="ghost" size="sm" onClick={() => insertTag('ul')} title="无序列表">
                    <List className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => insertTag('ol')} title="有序列表">
                    <ListOrdered className="h-4 w-4" />
                  </Button>
                  <div className="w-px h-6 bg-gray-300 mx-1" />
                  <Button type="button" variant="ghost" size="sm" onClick={() => insertTag('link')} title="链接">
                    <Link className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => contentImageInputRef.current?.click()} title="插入图片">
                    <ImageIcon className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => insertTag('br')} title="换行">
                    换行
                  </Button>
                  <input
                    ref={contentImageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleContentImageUpload}
                  />
                </div>
                <textarea
                  id="content"
                  className="w-full px-3 py-2"
                  rows={12}
                  value={formData.content || ''}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="请输入新闻正文内容，支持HTML标签..."
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">支持 HTML 标签，或使用上方工具栏插入格式</p>
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                取消
              </Button>
              <Button type="submit">
                <Calendar className="mr-2 h-4 w-4" />
                保存
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={errorDialog.open} onOpenChange={(open) => setErrorDialog({ ...errorDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              {errorDialog.title}
            </DialogTitle>
            <DialogDescription>
              {errorDialog.message}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={() => setErrorDialog({ ...errorDialog, open: false })}>
              知道了
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
